import { useCreateUserCalendar } from 'src/hooks/useCreateUserCalendar'
import { OnboardingStep } from '../../OnboardingStep/OnboardingStep'
import { getStepStatus } from '../utils'
import { toaster } from 'src/utils/toaster'
import { useSetAtom } from 'jotai'
import { ReactComponent as HelpIcon } from 'src/assets/svg/help.svg'
import { connectCalendarHelpModalAtom } from 'src/atoms/modals/connectCalendarHelpModal.atom'
import { CreateUserCalendarData } from 'src/api/onboarding/mutations/createUserCalendar'
import { isIOSDevice, isMacOSDevice } from 'src/utils/utils'
import { useGetUserCalendar } from 'src/hooks/useGetUserCalendar'
import { track } from 'src/shared/tracking'

import './CalendarStep.scss'

interface CalendarStepProps {
  readonly completed: boolean
  readonly enabled: boolean
}

export function CalendarStep({
  completed,
  enabled
}: Readonly<CalendarStepProps>) {
  const setConnectCalendarHelpModal = useSetAtom(connectCalendarHelpModalAtom)
  const { data: calendarData } = useGetUserCalendar()

  const { createUserCalendar, loading } = useCreateUserCalendar({
    onError,
    onCompleted
  })

  function handleCalendarSync() {
    createUserCalendar()
  }

  function onError() {
    toaster.error(`We couldn't connect your calendar. Please try again.`)
  }

  function onCompleted(result: CreateUserCalendarData) {
    const calendarUrl = result.createUserCalendar.calendarUrl
    connectCalendar(calendarUrl)
  }

  function handleCompletedClick() {
    connectCalendar(calendarData?.calendarS3Url ?? '')
  }

  function connectCalendar(url: string) {
    if (!url) {
      return
    }

    const finalUrl =
      (isIOSDevice() || isMacOSDevice()) && url.startsWith('https://')
        ? url.replace('https://', 'webcal://')
        : url

    // Add a small delay to ensure tracking completes before opening the window
    setTimeout(() => {
      window.open(finalUrl, '_blank')
    }, 100)
  }

  function handleHelpClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()

    track('CTA Button Clicked', {
      overlay_name: 'Onboarding RVMP - TODO list',
      overlay_class: 'modal',
      overlay_trigger: 'manual',
      cta_text: 'Trouble connecting your calendar?'
    })

    setConnectCalendarHelpModal({
      isOpen: true
    })
  }

  const status = getStepStatus(completed, enabled)

  return (
    <OnboardingStep
      className="calendar-step"
      title="Connect your calendar"
      description="Sync your calendar to stay on top of upcoming deadlines and deliveries."
      status={status}
      icon="calendar"
      action={{
        label: loading ? 'Connecting...' : 'Connect Calendar',
        onClick: completed ? handleCompletedClick : handleCalendarSync
      }}>
      {completed && (
        <button
          className="calendar-step__help-container"
          onClick={handleHelpClick}>
          <HelpIcon width={16} />
          Trouble connecting your calendar?
        </button>
      )}
    </OnboardingStep>
  )
}
